// ── Getting a visitor into the demo ───────────────────────────────────────────
//
// Runs once, before the app mounts. Everything it decides comes out of the URL
// (DEMOS.md §3), because the URL is the only thing a salesperson can build in
// front of a client, paste into an email, and read out over the phone.
//
//   /?m=crm,quotes    these modules
//   /?m=              (or no m at all) no choice made — the selector
//   /?lang=ca         the language to open in
//   /?ticket=…        the one-use code the public front page hands out
//                     (DEMOS.md §3): traded for a visit of fixed length
//
// On an instance that is not a demo this does nothing at all — and, since
// 0.0.2, it does nothing at all WITHOUT ASKING. See `askedAbout` below.

import { setTokens, clearSession, STORAGE } from '@general/ui-kit'

import { demoApi } from './api'
import { VISIT_KEY, requestedTicket, visitOver, withoutTicket } from './visit'

const SUPPORTED_LANGUAGES = ['es', 'ca', 'en', 'fr']

/**
 * Whether this origin has already been asked, and for which build.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * The catalogue call ran on every single boot of every client's ERP, and on
 * every one of them it answered 404. It was harmless by design — the answer is
 * "carry on as an ordinary ERP" — and it was still wrong for two reasons that
 * only show up in production:
 *
 *   · a 404 on every page load is a line in the client's access log every time
 *     anybody opens the ERP. A real 404, the day there is one, arrives into a
 *     log that has been crying wolf since the install;
 *   · it is a round trip on the critical path of the boot, awaited before the
 *     app mounts, spent to learn something that cannot change.
 *
 * ── Why the build id is part of the key ──────────────────────────────────────
 *
 * An origin is either a demo or it is not, and that does not change while a
 * browser tab is open — but it CAN change on a deploy, when somebody turns
 * `DEMO_MODE` on. Keying the memory to the running build means every deploy
 * asks once more, so turning the demo on is a redeploy and never "and clear
 * everybody's site data". Turning it off self-corrects even sooner: a stored
 * `true` still calls the catalogue, because the answer is what it needs.
 */
const ASKED_KEY = 'general.demo.asked'
const RUNNING_BUILD = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev'

/** True when this build already asked this origin and got a no. */
function knownNotADemo() {
  try {
    return localStorage.getItem(ASKED_KEY) === `no:${RUNNING_BUILD}`
  } catch {
    // A browser with storage blocked answers by throwing. Then it asks every
    // time, which is exactly what it did before this existed.
    return false
  }
}

function rememberNotADemo() {
  try { localStorage.setItem(ASKED_KEY, `no:${RUNNING_BUILD}`) } catch { /* storage blocked */ }
}

function forgetTheAnswer() {
  try { localStorage.removeItem(ASKED_KEY) } catch { /* storage blocked */ }
}

/**
 * Where the demo remembers it is a demo.
 *
 * Written as soon as the instance answers the catalogue, before any session
 * exists, because the router needs the answer SYNCHRONOUSLY: its guard has to
 * decide between sending somebody who is not signed in to the sign-in screen
 * or to the selector, and on a demo instance there is no sign-in screen to
 * send them to.
 */
export const DEMO_FLAG = 'general.demo'

// ── The visit ─────────────────────────────────────────────────────────────────
//
// How long it lasts, where it goes when it ends, who to ask for an instance and
// which industry's data it shows. Written from the catalogue on every boot and
// completed with `endsAt` when a session opens; read by the band.

function readVisit() {
  try { return JSON.parse(localStorage.getItem(VISIT_KEY) ?? 'null') ?? {} } catch { return {} }
}

function writeVisit(changes) {
  try { localStorage.setItem(VISIT_KEY, JSON.stringify({ ...readVisit(), ...changes })) } catch { /* storage blocked */ }
}

/** The current visit: `{ endsAt, exitUrl, contactUrl, directEntry, sector, visitor }`. */
export const demoVisit = () => readVisit()

/**
 * Ends the visit here and sends the visitor to the front page.
 *
 * Without a front page configured there is nowhere better than the selector,
 * which is what a demo did before there was one.
 */
export function leaveDemo() {
  clearSession()
  const { exitUrl } = readVisit()
  if (exitUrl) {
    window.location.replace(exitUrl)
    return true
  }
  return false
}

let listening = false
/**
 * Any lost session, on a demo, means the visit is over: there is no sign-in to
 * send anybody to. Installed once, before the app mounts; the shell's own
 * listener still runs and does no harm on its way.
 */
function leaveWhenTheSessionIsLost() {
  if (listening) return
  listening = true
  window.addEventListener('general:unauthorized', () => { leaveDemo() })
}

/** What `?m=` asked for, or null when it was not there at all. */
export function requestedModules(search = window.location.search) {
  const params = new URLSearchParams(search)
  if (!params.has('m')) return null

  return params.get('m')
    .split(',')
    .map(name => name.trim().toLowerCase())
    .filter(Boolean)
}

/** `?lang=`, when it names one of the four the SPA speaks. */
export function requestedLanguage(search = window.location.search) {
  const wanted = new URLSearchParams(search).get('lang')
  return SUPPORTED_LANGUAGES.includes(wanted) ? wanted : null
}

/**
 * True on an instance running with DEMO_MODE.
 *
 * Cached twice over: in memory for the life of the tab, and in storage for the
 * life of the build (see `ASKED_KEY`). A client's ERP therefore asks once, on
 * the first visit of each browser after each deploy, and never again.
 */
let cachedCatalogue
export async function demoCatalogue() {
  if (cachedCatalogue !== undefined) return cachedCatalogue

  // Asked before, on this build, and told no. Nothing to gain by asking again.
  if (knownNotADemo()) {
    cachedCatalogue = null
    return cachedCatalogue
  }

  let answered = false
  try {
    cachedCatalogue = await demoApi.catalogue()
  } catch (error) {
    // Both a 404 and a dead network mean the same thing for THIS boot — carry
    // on as an ordinary ERP — and they must not mean the same thing for the
    // next one.
    //
    // A 404 is the instance ANSWERING: there is no demo here, and there will
    // not be one until somebody deploys. Worth remembering.
    //
    // A network failure, a 502 or a timeout is the instance not answering at
    // all — which is what a browser sees when it boots while the API is
    // restarting. Remembering that would decide, for the rest of the build,
    // that a demo instance is not one; a salesperson would open the link in
    // front of a client and get a sign-in screen that does not exist.
    answered = Number.isFinite(error?.status) && error.status >= 400 && error.status < 500
    cachedCatalogue = null
  }

  if (cachedCatalogue) forgetTheAnswer()
  else if (answered) rememberNotADemo()

  return cachedCatalogue
}

/**
 * True when this instance is a demo. Synchronous, so the router can ask it.
 *
 * False on a real client's ERP for ever: nothing writes the flag there, because
 * the catalogue call that would have written it answered 404.
 */
export const isDemoInstance = () => localStorage.getItem(DEMO_FLAG) === 'true'

/**
 * Why the last attempt to open a demo session failed, or null.
 *
 * It exists because of the way this breaks in practice. The instance is a
 * demo, the catalogue loads, the selector paints — and pressing Enter does
 * nothing at all, because the API refused the POST for a reason only its log
 * knows (its CORS list not naming the address people actually type is the one
 * that has happened). A screen that says nothing is being shown to somebody
 * who is deciding whether to buy this.
 *
 * A plain module-level value and not a store: it is written before Pinia
 * exists.
 */
let failure = null
export const demoFailure = () => failure

/**
 * Opens a demo session and stores it exactly as a sign-in would.
 *
 * Called before the app mounts, so the router's first navigation already sees
 * an authenticated user and nobody watches a login screen flash past.
 *
 * @returns {Promise<'demo'|'selector'|'none'>} what the shell should do next
 */
export async function ensureDemoSession() {
  const catalogue = await demoCatalogue()
  if (!catalogue) {
    // Not a demo. Clear the flag rather than leave it: a browser that visited
    // the demo and then opens a client's instance must not carry the band over.
    localStorage.removeItem(DEMO_FLAG)
    return 'none'
  }

  localStorage.setItem(DEMO_FLAG, 'true')

  const visit = catalogue.visit ?? {}
  writeVisit({
    exitUrl: visit.exitUrl ?? '',
    contactUrl: visit.contactUrl ?? '',
    directEntry: visit.directEntry !== false,
    sector: visit.sector ?? null,
    minutes: visit.minutes ?? null,
  })
  leaveWhenTheSessionIsLost()

  const ticket = requestedTicket(window.location.search)
  const modules = requestedModules()
  // The key the shell reads on boot (platform/web/frontend/app/languages.js).
  // Written rather than imported: the shell depends on this package, not the
  // other way round, and one constant is a smaller price than that cycle.
  const language = requestedLanguage()
  if (language) localStorage.setItem('general.locale', language)

  // ── From the front page ────────────────────────────────────────────────────
  // The code is spent the moment it is read, and taken out of the address bar
  // before anything else happens: a reload, a bookmark or a copied link must
  // carry nothing that opens a visit.
  if (ticket) {
    clearSession()
    try { window.history.replaceState(window.history.state, '', withoutTicket(window.location.href)) } catch { /* old browser */ }
    try {
      return storeVisitSession(await demoApi.redeem(ticket))
    } catch (error) {
      failure = error
      // Used, expired or invented: the front page is where a new one comes from.
      if (leaveDemo()) return 'none'
      return 'selector'
    }
  }

  // A visit already open survives a reload — until its end.
  if (localStorage.getItem(STORAGE.access) && visitOver(readVisit().endsAt)) {
    if (leaveDemo()) return 'none'
  }

  // With a front page, it is the only door: nobody opens a visit by hand.
  if (visit.directEntry === false) {
    if (localStorage.getItem(STORAGE.access)) return 'demo'
    if (leaveDemo()) return 'none'
    return 'selector'
  }

  // No `m` and no session: there is nothing to show yet, and the selector is
  // the screen that asks. With a session already open, a plain reload keeps it.
  if (modules === null) {
    return localStorage.getItem(STORAGE.access) ? 'demo' : 'selector'
  }

  // `m` is present, so the URL is making the decision — even over a session
  // that is already open. That is how a salesperson moves from one client's
  // set of modules to the next without hunting for a sign-out button.
  clearSession()

  let session
  try {
    session = await demoApi.openSession(modules)
  } catch (error) {
    // Not thrown onwards: the app still has to render, because the screen it
    // renders is the one that says what went wrong.
    failure = error
    return 'selector'
  }

  return storeVisitSession(session)
}

/** Stores a demo session exactly as a sign-in would, plus when it ends. */
function storeVisitSession(session) {
  setTokens(session)
  localStorage.setItem(STORAGE.user, JSON.stringify(session.user))
  writeVisit({ endsAt: session.endsAt ?? null, visitor: session.visitor ?? null })
  failure = null
  return 'demo'
}

/**
 * The link a salesperson copies. Built here so one file owns the URL shape.
 *
 * Con el PREFIJO de la aplicacion, y no solo el origen. Una instancia puede no
 * vivir en la raiz —la demo de obra cuelga de /construccion/— y con el origen a
 * secas cada boton de este selector, el de entrar y el de copiar, mandaba al
 * visitante a la OTRA demo del mismo dominio. No fallaba nada: se veia un ERP,
 * pero no el que se estaba enseñando.
 *
 * `import.meta.env.BASE_URL` es el mismo numero que usan los assets y el
 * enrutador, asi que los tres no pueden discrepar.
 */
export function demoLink(modules, {
  origin = window.location.origin,
  base = import.meta.env.BASE_URL ?? '/',
  // Si el enlace es para ENTRAR ahora o para copiarlo y mandarlo.
  //
  // La diferencia está solo en el caso de «todo el ERP», y es la que tenía el
  // botón principal de la demostración muerto: sin módulos elegidos el enlace
  // salía como `…/construccion/`, y una URL sin `m` significa exactamente
  // «enséñame el selector» —`requestedModules()` devuelve null cuando el
  // parámetro no está—. Así que pulsar «Ver el ERP entero» recargaba el
  // selector, y volvía a recargarlo, para siempre. Sin un solo error en
  // ninguna consola: la petición que habría fallado no llegaba a hacerse.
  //
  // `?m=` presente y vacío sí es una decisión: la lista de módulos pedida está
  // vacía, y una lista vacía es el ERP entero (`resolveModules`). Por eso
  // entrar lo pone siempre y copiar no: un enlace que alguien recibe por correo
  // es mejor que abra el selector.
  entering = false,
} = {}) {
  // Sin la barra final, que la pone quien construye la ruta de abajo.
  const root = `${origin}${base}`.replace(/\/+$/, '')
  const chosen = [...new Set(modules)].sort()
  if (chosen.length) return `${root}/?m=${chosen.join(',')}`
  return entering ? `${root}/?m=` : `${root}/`
}
